import { FC, ReactElement, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Text
} from 'recharts';
import ChartTooltip from './ChartTooltip';

export type LinePoint = { label: string; value: number };

type MetricLineChartProps = {
  data: LinePoint[];
  label: string;
  subLabel?: string;
};

const MetricLineChart: FC<MetricLineChartProps> = ({ data, label, subLabel }): ReactElement => {
  const chartData = data.map((d) => ({ name: d.label, value: d.value }));
  const [tooltipY, setTooltipY] = useState<number | undefined>(undefined);
  const [tooltipX, setTooltipX] = useState<number | undefined>(undefined);

  const CustomXAxisTick = ({
    x,
    y,
    payload
  }: {
    x: number;
    y: number;
    payload: { value: string };
  }) => {
    if (!payload?.value) return null;
    return (
      <Text
        x={x}
        y={y + 5}
        textAnchor="middle"
        verticalAnchor="start"
        fill="var(--color-neutral-500)"
        className="text-xs"
      >
        {payload.value}
      </Text>
    );
  };

  const CustomYAxisTick = ({
    x,
    y,
    payload
  }: {
    x: number;
    y: number;
    payload: { value: string | number };
  }) => {
    if (payload === undefined || payload === null) return null;
    return (
      <Text
        x={x - 15}
        y={y}
        textAnchor="end"
        verticalAnchor="middle"
        fill="var(--color-neutral-500)"
        className="text-xs"
      >
        {payload.value}
      </Text>
    );
  };

  return (
    <div
      className={`
        relative flex w-438 h-306 p-20 flex-col items-start gap-2.5
        shrink-0 bg-white rounded-sm elevated
      `}
    >
      <div className="flex justify-between mb-10">
        <h3 className="text-h3">{label}</h3>
      </div>
      <div className="flex w-full">
        <ResponsiveContainer width="100%" height={210}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: -20, bottom: 10 }}>
            <CartesianGrid stroke="var(--color-neutral-100)" vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={{ stroke: 'var(--color-white)' }}
              axisLine={{ stroke: 'var(--color-neutral-100)' }}
              interval={0}
              tick={(props) => <CustomXAxisTick {...props} />}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={(props) => <CustomYAxisTick {...props} />}
            />
            <Tooltip
              cursor={false}
              content={<ChartTooltip />}
              isAnimationActive={false}
              useTranslate3d={false}
              position={
                tooltipX !== undefined && tooltipY !== undefined
                  ? { x: tooltipX - 25, y: tooltipY - 48 }
                  : undefined
              }
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--color-accent-2-500)"
              strokeWidth={3}
              dot={{ r: 4, stroke: 'var(--color-accent-2-500)' }}
              activeDot={(props: any) => {
                if (typeof props?.cx === 'number' && props.cx !== tooltipX) {
                  setTooltipX(props.cx);
                }
                if (typeof props?.cy === 'number' && props.cy !== tooltipY) {
                  setTooltipY(props.cy);
                }
                return (
                  <circle cx={props.cx} cy={props.cy} r={5} fill="var(--color-accent-2-500)" />
                );
              }}
              name="Games"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {subLabel ? (
        <label className="flex items-center text-xs italic text-neutral-300 pt-4">{subLabel}</label>
      ) : null}
    </div>
  );
};

export default MetricLineChart;
